import React, { useMemo, useState } from 'react';
import { Typography, Button, BaseIcon, Table, ScaleLogoLoading } from '@scale/scaleui';
import Link from '@scale/frontend-lib/link';
import { useGetChatBulkAudits } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import ChatBulkAuditModal from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/DataQuality/ChatBulkAuditModal';
import { AuditViewType } from '@scale/shared/types/corp/attemptAudit';
import { BOT_EMAIL } from '@scale/shared/defaults';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import ChatBulkAuditGoalLoading from './ChatBulkAuditGoalLoading';

interface TableData {
  auditName: React.ElementType;
  auditor: string;
  auditType: string;
  auditDate: string;
  tasksAudited: string;
}

export type ExternalAuditViewType = Exclude<AuditViewType, AuditViewType.SME>;

export const hrefMapping: Record<ExternalAuditViewType, string> = {
  chat_bulk_audit: '/tools/chat_bulk_audit/',
  standard: '/tools/speed_auditv2/',
  text_based: '/tools/speed_auditv2/',
};

const auditTypeMapping: Record<ExternalAuditViewType, string> = {
  chat_bulk_audit: 'Chat Bulk Audit',
  standard: 'Standard Speed Audit',
  text_based: 'Text Based Speed Audit',
};

interface ExternalChatBulkAuditsTableViewProps {
  userCanCreate: boolean;
  userId: string;
  email: string;
  url: {
    query: {
      project: string;
      goal?: string;
    };
  };
}

const tableColumns = [
  { header: 'Audit Name', id: 'auditName' },
  { header: 'Auditor', id: 'auditor' },
  { header: 'Audit Type', id: 'auditType' },
  { header: 'Audit Date', id: 'auditDate' },
  { header: 'Tasks Audited', id: 'tasksAudited' },
];

const ExternalChatBulkAuditsTableView: React.FC<ExternalChatBulkAuditsTableViewProps> = ({
  userCanCreate,
  email,
  url,
  userId,
}) => {
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const auditor = email;
  const projectId = url.query.project;
  const goalId = url.query.goal;
  const isOutlierAdmin = window.location.pathname.includes('/outlieradmin');
  const urlPrefix = isOutlierAdmin ? '/expert/outlieradmin' : '/remoadmin';
  const { data: audits, isLoading: isLoadingAudits } = useGetChatBulkAudits({ projectId });
  const showBotCreatedAudits = useUnleashFeatureFlag('show-bot-created-audits', {}, false);

  const auditBatches: TableData[] = useMemo(
    () =>
      (
        audits?.map((batch: any) => {
          const auditViewType: ExternalAuditViewType = batch.auditViewType;
          const href =
            auditViewType in hrefMapping
              ? hrefMapping[auditViewType]
              : urlPrefix + '/tools/chat_bulk_audit';
          const auditType =
            auditViewType in auditTypeMapping ? auditTypeMapping[auditViewType] : '';

          return {
            auditName: (
              <Button size="sm" variant="transparent" className="shadow-none border-none p-0">
                <Link href={`${urlPrefix}${href}/${batch._id}`}>
                  <a className="flex items-center" target="_blank" rel="noopener noreferrer">
                    {batch.name}
                  </a>
                </Link>
              </Button>
            ),
            auditor: batch.auditedBy.email,
            auditType,
            auditDate: new Date(batch.createdAt).toLocaleString('en-US'),
            tasksAudited: batch.completed
              ? String(batch.attemptAudits?.length ?? 'Error')
              : 'Not Completed',
          };
        }) || []
      ).sort(
        (a: TableData, b: TableData) =>
          new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime(),
      ),
    [audits, urlPrefix],
  );

  const currentUserAuditBatches = useMemo(
    () =>
      auditBatches.filter(
        (batch: TableData) =>
          (auditor?.length
            ? batch.auditor === auditor || (showBotCreatedAudits && batch.auditor === BOT_EMAIL)
            : true) && batch.auditType,
      ),
    [auditBatches, auditor, showBotCreatedAudits],
  );

  return goalId ? (
    <div className="h-full flex flex-col">
      <ChatBulkAuditGoalLoading projectId={projectId} userId={userId} goalId={goalId} />
    </div>
  ) : (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-x-auto py-8 px-14 space-y-8">
        <div className="flex flex-rows justify-between items-center">
          <Typography.H3>Audit History</Typography.H3>
          {userCanCreate && (
            <Button
              onClick={() => setIsCreationModalOpen(true)}
              className="flex gap-1 items-center my-4"
            >
              <BaseIcon edge="start" icon={['fas', 'plus']} variant="white" />
              Create New Bulk Audit
            </Button>
          )}
        </div>
        {isLoadingAudits ? (
          <ScaleLogoLoading size="lg" />
        ) : (
          <>
            <ChatBulkAuditModal
              isOpen={isCreationModalOpen}
              onClose={() => setIsCreationModalOpen(false)}
              projectId={projectId}
              isExternal={true}
            />
            <Table
              className=""
              columns={tableColumns}
              data={currentUserAuditBatches}
              defaultPageSize={10}
              divideColumns
              divideRows
              stripedRows={false}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalChatBulkAuditsTableView;
